<template>
  <AppDialog
    class="rate-review-dialog"
    title="Review your changes"
    size="medium"
    position="centered"
    show
    append-to-body
    data-testid="rate-review-dialog"
    @close="cancel"
  >
    <template #header>
      <p class="modal-subtitle rate-review-subtitle">Review the changes below for <strong class="rate-review-member-name">{{ displayName }}</strong> before saving.</p>
    </template>

    <div
      v-for="section in sections"
      :key="section.key"
      class="rate-review-section"
    >
      <p class="rate-review-section-title">{{ sectionTitle(section.key) }}</p>
      <div class="rate-review-row">
        <div
          class="rate-review-card"
          :class="{ 'rate-review-card--empty': !section.current }"
        >
          <p class="rate-review-card-label">Current</p>
          <template v-if="section.current">
            <p class="rate-review-amount">
              <span class="rate-review-currency">{{ currencyCode }}</span>
              <span>
                {{ amountText(section.current.amount) }}
              </span>
              <span class="rate-review-unit">/ {{ section.current.unit }}</span>
            </p>
            <p
              v-if="section.showPeriod"
              class="rate-review-period"
            >
              {{ section.current.period }}
            </p>
            <div class="rate-review-date">
              <i class="hi hi-calendar hi-12 mr-5" />
              <span class="rate-review-date-label">Effective date</span>
              <span class="rate-review-date-value">{{ formatDate(section.current.startDateIso) }}</span>
            </div>
          </template>
          <p
            v-else
            class="rate-review-empty"
          >
            Not set
          </p>
        </div>

        <span
          class="material-symbols-rounded rate-review-arrow"
          aria-hidden="true"
        >arrow_forward</span>

        <div class="rate-review-card rate-review-card--updated">
          <p class="rate-review-card-label">Updated</p>
          <p class="rate-review-amount">
            <span class="rate-review-currency">{{ currencyCode }}</span>
            <span :class="{ 'rate-change--highlight': section.changed.amount && !section.unusual, 'rate-change--unusual': !!section.unusual }">
              {{ amountText(section.updated.amount) }}
            </span>
            <span
              class="rate-review-unit"
              :class="{ 'rate-change--highlight': section.changed.unit }"
            >
              / {{ section.updated.unit }}
            </span>
          </p>
          <p
            v-if="section.showPeriod"
            class="rate-review-period"
            :class="{ 'rate-change--highlight': section.changed.period }"
          >
            {{ section.updated.period }}
          </p>
          <div class="rate-review-date">
            <i class="hi hi-calendar hi-12 mr-5" />
            <span class="rate-review-date-label">Effective date</span>
            <span
              class="rate-review-date-value"
              :class="{ 'rate-change--highlight': section.changed.date }"
            >
              {{ updatedDateText(section) }}
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="section.unusual"
        class="rate-review-unusual"
        role="alert"
        data-testid="rate-review-unusual-alert"
      >
        <p class="rate-review-unusual-title">
          <i class="hi hi-warning hi-16 mr-5" />
          Unusual {{ unusualLabel(section) }}
        </p>
        <p class="rate-review-unusual-text">{{ unusualMessage(section) }}</p>
      </div>
    </div>

    <template #footer>
      <AppButton
        type="default"
        class="mr-10"
        data-testid="rate-review-cancel"
        @click="cancel"
      >
        Cancel
      </AppButton>
      <AppButton
        type="primary"
        data-testid="rate-review-confirm"
        @click="confirm"
      >
        Save changes
      </AppButton>
    </template>
  </AppDialog>
</template>

<script>
import moment from 'moment';

import currencyFilter from '~/filters/currency';

const DATE_FORMAT = 'ddd, MMM D, YYYY';

export default {
  name: 'MemberRatesReviewDialog',
  props: {
    sections: {
      type: Array,
      required: true,
    },
    memberName: {
      type: String,
      default: '',
    },
    currency: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['confirm', 'cancel'],
  computed: {
    displayName() {
      return this.memberName || 'this member';
    },
    symbol() {
      return this.currency?.symbol ?? '';
    },
    currencyCode() {
      return this.currency?.currency ?? this.symbol;
    },
  },
  methods: {
    // Formats the number without the currency symbol so the symbol can stay un-highlighted.
    amountText(amount) {
      if (amount == null) { return '—'; }

      return currencyFilter(amount, { ...this.currency, symbol: '' }).trim();
    },
    // Currency code and amount, space-separated (e.g. "USD 1,000.00").
    rateText(amount) {
      return `${this.currencyCode} ${this.amountText(amount)}`.trim();
    },
    unusualLabel(section) {
      return section.key === 'pay' ? 'pay rate' : 'bill rate';
    },
    unusualMessage(section) {
      const label = this.unusualLabel(section);
      const entered = this.rateText(section.unusual.enteredAmount);
      const previous = this.rateText(section.unusual.previousAmount);

      return `This ${label} (${entered}/hr) is much higher than this member's previous ${label} (${previous}/hr).`;
    },
    sectionTitle(key) {
      return key === 'pay' ? 'Pay rate changes' : 'Bill rate changes';
    },
    // A null ISO date means "effective today" (the form default when the user picks no date).
    formatDate(iso) {
      return moment(iso || undefined).format(DATE_FORMAT);
    },
    // The section model already resolves the effective date to the chosen date (or today when none
    // was picked), so just format it.
    updatedDateText(section) {
      return this.formatDate(section.updated.startDateIso);
    },
    confirm() {
      this.$emit('confirm');
    },
    cancel() {
      this.$emit('cancel');
    },
  },
};
</script>
